/**
 * The `path:line` convention, in one place.
 *
 * `/home/user/repo/src/app.ts:42` and `/home/user/repo/src/app.ts:42-88` are
 * how every tool in this ecosystem — ripgrep, editors, stack traces, the
 * agent's own prose — names a *region* of a file rather than the whole thing.
 * Three surfaces need to agree on it: the filesystem plugin's path scanner
 * (which must match the suffix so the popover can scroll to it), `ScannedPath`
 * (which composes one for a caller that already has the numbers in hand), and
 * the work-assessment document (whose file refs carry the region an assessment
 * actually wants read).
 *
 * Those are two plugins and `@reflex/ui`, so the parser lives here rather than
 * in either plugin: three copies of "what counts as a line suffix" is three
 * chances for the scanner to match a range the preview then refuses to parse.
 * It sits in `@reflex/shared` rather than `@reflex/ui/client` because the
 * filesystem plugin's scanner pattern is built in `web-manifest.ts`, which the
 * Fastify process imports when it loads the plugin — and the server's only
 * workspace edge is to `@reflex/shared`.
 *
 * Parsing is deliberately conservative — a suffix that isn't a well-formed
 * range is left as part of the path. A file really can be named `notes:2024`,
 * and mangling its path is worse than not linking a line.
 */

/** An inclusive, 1-based run of lines. */
export interface LineRange {
  startLine: number;
  /** Omitted when the range is a single line. */
  endLine?: number;
}

/** A file path, optionally narrowed to a line or a line range. */
export interface FileLocation extends Partial<LineRange> {
  /** Absolute path, with any `:line` suffix stripped. */
  path: string;
}

/**
 * Upper bound on a line number we will believe. Well past the largest file any
 * preview will mount (the code viewer caps at 2,000 rows), and low enough that
 * a long digit run in a filename can't be read as a line number.
 */
const MAX_LINE = 1_000_000;

/**
 * Regex source for the optional suffix, so a scanner pattern can opt into
 * matching `…/app.ts:42-88` as one token. Non-capturing, and un-anchored, so
 * it composes into a larger pattern without disturbing its group numbering.
 */
export const LINE_SUFFIX_PATTERN = '(?::\\d+(?:-\\d+)?)?';

/** Anchored form used to pull the suffix back off a matched token. */
const LINE_SUFFIX_RE = /:(\d+(?:-\d+)?)$/;

/** The range on its own (`42`, `42-88`) — the suffix without its colon. */
const LINE_RANGE_RE = /^(\d+)(?:-(\d+))?$/;

function toLine(raw: string | undefined): number | null {
  if (raw === undefined) return null;
  const value = Number(raw);
  if (!Number.isInteger(value) || value < 1 || value > MAX_LINE) return null;
  return value;
}

/**
 * Parse a bare `42` / `42-88` range, the form carried in a URL query param.
 *
 * Returns `null` for anything that isn't a well-formed range. A reversed range
 * is ordered rather than rejected — `88-42` is a typo with an obvious intent —
 * and a range whose end equals its start collapses to a single line, so
 * formatting and rendering have one representation to handle.
 */
export function parseLineRange(raw: string | null | undefined): LineRange | null {
  if (!raw) return null;
  const parts = LINE_RANGE_RE.exec(raw);
  if (!parts) return null;
  const start = toLine(parts[1]);
  if (start === null) return null;
  const end = toLine(parts[2]);
  if (parts[2] !== undefined && end === null) return null;
  if (end === null || end === start) return { startLine: start };
  return end < start ? { startLine: end, endLine: start } : { startLine: start, endLine: end };
}

/**
 * Split a `path[:start[-end]]` token into its parts.
 *
 * Returns the input as a bare `path` when there is no suffix, when the suffix
 * isn't a valid range, or when the `:` sits in a directory segment — a token
 * like `/tmp/build:1/out.txt` names a file inside an oddly-named folder, not a
 * line.
 */
export function parseFileLocation(raw: string): FileLocation {
  const suffix = LINE_SUFFIX_RE.exec(raw);
  if (!suffix) return { path: raw };

  const head = raw.slice(0, suffix.index);
  // Requires a `/` before the suffix, which rules out both the mid-path case
  // above and the degenerate `:12` with no path at all.
  if (!head.includes('/')) return { path: raw };

  const range = parseLineRange(suffix[1]);
  return range ? { path: head, ...range } : { path: raw };
}

/**
 * The inverse of {@link parseFileLocation}: the canonical `path:start-end`
 * token. This is what gets handed to the content-scanner registry, so it must
 * round-trip.
 */
export function formatFileLocation(location: FileLocation): string {
  const range = formatLineRangeParam(location.startLine, location.endLine);
  return range ? `${location.path}:${range}` : location.path;
}

/** The range half of a location, as it appears in a URL param: `42` / `42-88`. */
export function formatLineRangeParam(startLine?: number, endLine?: number): string | null {
  if (!startLine) return null;
  return endLine && endLine !== startLine ? `${startLine}-${endLine}` : `${startLine}`;
}

/**
 * Human-facing label for the region: `L42` or `L42–88`. Uses an en dash
 * because this is read, not parsed — {@link formatFileLocation} owns the
 * machine form.
 */
export function formatLineRange(startLine?: number, endLine?: number): string | null {
  if (!startLine) return null;
  return endLine && endLine !== startLine ? `L${startLine}–${endLine}` : `L${startLine}`;
}

/** How many lines the region covers, for "42 lines" style copy. */
export function lineRangeLength(startLine?: number, endLine?: number): number {
  if (!startLine) return 0;
  return endLine && endLine > startLine ? endLine - startLine + 1 : 1;
}
